/**
 * Utility functions for the EditCustomersForm component.
 */

import { Account__CustomerInput } from '@admin/schema';

import { Customer, Phone } from './types';

let customerCounter = 0;
let phoneCounter = 0;

/**
 * Creates a new empty phone object with a unique temp ID.
 *
 * @returns A new empty Phone object.
 */
export const getEmptyPhone = (): Phone => {
  phoneCounter++;
  return {
    kind: 'unknown',
    number: '',
    primary: false,
    tempId: `new-${phoneCounter}`,
  };
};

/**
 * Creates a new empty customer object with a unique temp ID.
 *
 * @param accountId - The account ID to associate with the customer.
 * @returns A new empty Customer object.
 */
export const getEmptyCustomer = (accountId: number): Customer => {
  customerCounter++;
  return {
    account_id: accountId,
    email: '',
    name: '',
    phones: [getEmptyPhone()],
    primary_email: false,
    tempId: `new-${customerCounter}`,
  };
};

/**
 * Checks if a phone is empty (no ID and no number).
 *
 * @param phone - The phone to check.
 * @returns True if the phone is empty.
 */
export const isEmptyPhone = (phone: Phone): boolean => !phone.id && !phone.number;

/**
 * Checks if a customer has at least one non-empty phone.
 *
 * @param customer - The customer to check.
 * @returns True if the customer has at least one non-empty phone.
 */
export const hasNonEmptyPhone = (customer: Customer): boolean => customer.phones.some((phone) => !isEmptyPhone(phone));

/**
 * Checks if a customer is empty (no ID, name, email, or non-empty phones).
 *
 * @param customer - The customer to check.
 * @returns True if the customer is empty.
 */
export const isEmptyCustomer = (customer: Customer): boolean =>
  !customer.id && !customer.name && !customer.email && !hasNonEmptyPhone(customer);

/**
 * Checks if a customer is the primary customer (has a primary phone).
 *
 * @param customer - The customer to check.
 * @returns True if the customer is the primary customer.
 */
export const isPrimaryCustomer = (customer: Customer): boolean =>
  customer.phones?.some((phone) => phone.primary) ?? false;

/**
 * Filters out deleted phones from a list.
 *
 * @param phones - The list of phones to filter.
 * @returns The list of non-deleted phones.
 */
export const getNonDeletedPhones = (phones: Phone[]): Phone[] => phones.filter((phone) => !phone.deleted);

/**
 * Sorts phones with primary phone first, then alphabetically by number.
 *
 * @param phones - The list of phones to sort.
 * @returns The sorted list of phones.
 */
const sortPhonesForOneCustomer = (phones: Phone[]): Phone[] => {
  const primaryIndex = phones.findIndex((phone) => phone.primary);
  const result = [...phones];

  if (primaryIndex === -1) {
    return result.sort((a, b) => a.number.localeCompare(b.number));
  }

  const primaryPhone = result.splice(primaryIndex, 1)[0];
  result.sort((a, b) => a.number.localeCompare(b.number));
  result.unshift(primaryPhone);
  return result;
};

/**
 * Sorts customers with primary customer first, then alphabetically by name.
 *
 * @param customers - The list of customers to sort.
 * @returns The sorted list of customers.
 */
const sortForCustomerList = (customers: Customer[]): Customer[] => {
  const primaryIndex = customers.findIndex(isPrimaryCustomer);
  const result = [...customers];

  if (primaryIndex !== -1) {
    const primaryCustomer = result.splice(primaryIndex, 1)[0];
    result.sort((a, b) => a.name.localeCompare(b.name));
    result.unshift(primaryCustomer);
  } else {
    result.sort((a, b) => a.name.localeCompare(b.name));
  }

  return result;
};

/**
 * Prepares customer data for editing by sorting and adding temp IDs.
 *
 * @param customerData - The raw customer data from the API.
 * @returns The prepared customer data for editing.
 */
export const getCustomersToEdit = (customerData: Customer[]): Customer[] => {
  let customers = JSON.parse(JSON.stringify(customerData)) as Customer[];
  customers = sortForCustomerList(customers);
  customers.forEach((customer) => {
    customer.phones = customer.phones.length
      ? sortPhonesForOneCustomer(customer.phones).map((phone) => ({
          ...phone,
          tempId: `existing-${phone.id}`,
        }))
      : [getEmptyPhone()];
    customer.tempId = `existing-${customer.id}`;
  });
  return customers;
};

/**
 * Prepares customer data for saving by removing empty entries.
 *
 * @param customerData - The customer data from the form.
 * @returns The prepared customer data for saving.
 */
export const getCustomersToSave = (customerData: Customer[]): Customer[] => {
  let customers = JSON.parse(JSON.stringify(customerData)) as Customer[];

  // Remove any "empty" customers
  customers = customers.filter((customer) => !isEmptyCustomer(customer));

  // Remove any "empty" phones and set primary to false if nil
  customers.forEach((customer) => {
    customer.phones = customer.phones.filter((phone) => !isEmptyPhone(phone));
    customer.phones.forEach((phone) => {
      if (phone.primary === undefined || phone.primary === null) {
        phone.primary = false;
      }
    });
  });

  return customers;
};

/**
 * Finds duplicate emails in the customer list.
 *
 * @param customers - The list of customers to check.
 * @returns Array of duplicate email addresses.
 */
const getDuplicateEmails = (customers: Customer[]): string[] => {
  const emailCounts: Record<string, number> = {};
  customers.forEach((customer) => {
    if (customer.email) {
      emailCounts[customer.email] = (emailCounts[customer.email] || 0) + 1;
    }
  });
  return Object.keys(emailCounts).filter((email) => emailCounts[email] > 1);
};

/**
 * Finds duplicate phone numbers in the customer list.
 *
 * @param customers - The list of customers to check.
 * @returns Array of duplicate phone numbers.
 */
const getDuplicatePhones = (customers: Customer[]): string[] => {
  const phoneCounts: Record<string, number> = {};
  customers.forEach((customer) => {
    getNonDeletedPhones(customer.phones)
      .filter((phone) => phone.number)
      .forEach((phone) => {
        phoneCounts[phone.number] = (phoneCounts[phone.number] || 0) + 1;
      });
  });
  return Object.keys(phoneCounts).filter((number) => phoneCounts[number] > 1);
};

/**
 * Validates customers for duplicates.
 *
 * @param customers - The list of customers to validate.
 * @returns Validation result with isValid flag and error messages.
 */
export const validateCustomers = (customers: Customer[]): { isValid: boolean; messages: string[] } => {
  const duplicateEmails = getDuplicateEmails(customers);
  const duplicatePhones = getDuplicatePhones(customers);
  const messages: string[] = [];

  duplicateEmails.forEach((email) => {
    messages.push(
      `It looks like you entered email ${email} multiple times. Please remove the duplicates and try saving again.`,
    );
  });

  duplicatePhones.forEach((phone) => {
    messages.push(
      `It looks like you entered phone ${phone} multiple times. Please remove the duplicates and try saving again.`,
    );
  });

  return {
    isValid: messages.length === 0,
    messages,
  };
};

/**
 * Transforms a phone from component format to GraphQL input format.
 *
 * @param phone - Phone data in component format.
 * @returns Phone data in GraphQL input format.
 */
const buildPhoneInput = (phone: Phone) => ({
  id: phone.id?.toString(),
  commercialCall: phone.commercial_call,
  commercialSMS: phone.commercial_sms,
  deleted: phone.deleted,
  kind: phone.kind,
  number: phone.number,
  primary: phone.primary,
  transactionalCall: phone.transactional_call,
  transactionalSMS: phone.transactional_sms,
});

/**
 * Transforms a customer from component format to GraphQL input format.
 *
 * @param customer - Customer data in component format.
 * @returns Customer data in GraphQL input format.
 */
export const buildCustomerInput = (customer: Customer): Account__CustomerInput => ({
  id: customer.id?.toString(),
  email: customer.email,
  name: customer.name,
  phones: customer.phones.map(buildPhoneInput),
  primaryEmail: customer.primary_email,
});

/**
 * Humanizes a string by replacing underscores with spaces and capitalizing the first letter of each word.
 * Properly capitalizes known acronyms like SMS.
 *
 * @param str - The string to humanize.
 * @returns The humanized string.
 */
export const humanize = (str: string): string => {
  const acronyms = ['sms'];
  return str.replace(/_/g, ' ').replace(/\b\w+/g, (word) => {
    if (acronyms.includes(word.toLowerCase())) {
      return word.toUpperCase();
    }
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
};
